<template>
  <div class="galeria-fotos-grid">
    <div
      v-for="(item, index) in itens"
      :key="getKey(item, index)"
      class="galeria-card"
      @click="$emit('abrir', item)"
    >
      <div class="foto-imagem-wrapper">
        <img
          :src="getUrl(item)"
          :alt="getAlt(item)"
          loading="lazy"
        />

        <!-- Slot para badge personalizada (ex.: Pública / Privada) -->
        <slot
          name="badge"
          :item="item"
        />

        <!-- Overlay de zoom / visualização -->
        <slot
          name="overlay"
          :item="item"
        >
          <div class="foto-hover-overlay">
            <span class="btn-zoom-preview">
              <i class="ti ti-zoom-in"></i> Ver ampliada
            </span>
          </div>
        </slot>
      </div>

      <div class="foto-detalhes">
        <slot
          name="descricao"
          :item="item"
        >
          <p
            class="foto-descricao"
            :title="getDescricao(item)"
          >
            {{ getDescricao(item) || "Sem descrição" }}
          </p>
        </slot>

        <!-- Slot para rodapé interativo (ex.: data e botão remover) -->
        <div
          v-if="$slots.rodape"
          class="foto-rodape-container"
          @click.stop
        >
          <slot
            name="rodape"
            :item="item"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  itens: {
    type: Array,
    default: () => [],
  },
  resolverUrl: {
    type: Function,
    default: null,
  },
  obterDescricao: {
    type: Function,
    default: null,
  },
  obterAlt: {
    type: Function,
    default: null,
  },
  obterKey: {
    type: Function,
    default: null,
  },
});

defineEmits(["abrir"]);

function getUrl(item) {
  if (props.resolverUrl) return props.resolverUrl(item);
  if (!item) return "";
  if (item.imagemUrl) return item.imagemUrl;
  if (item.url) return item.url;
  if (item.imagem?.url) return item.imagem.url;
  if (item.imagem?.base64Data) {
    return `data:${item.imagem.mimeType || "image/jpeg"};base64,${item.imagem.base64Data}`;
  }
  if (item.dados) return `data:image/jpeg;base64,${item.dados}`;
  if (item.caminho) return item.caminho;
  return "";
}

function getDescricao(item) {
  if (props.obterDescricao) return props.obterDescricao(item);
  return item?.descricao || "";
}

function getAlt(item) {
  if (props.obterAlt) return props.obterAlt(item);
  return item?.descricao || "Foto";
}

function getKey(item, index) {
  if (props.obterKey) return props.obterKey(item, index);
  return item?.id || item?.acaoImagemId || index;
}
</script>

<style scoped>
.galeria-fotos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 18px;
}

.galeria-card {
  background: #ffffff;
  border: 1.5px solid hsl(258, 25%, 90%);
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 8px -2px rgba(54, 32, 101, 0.06);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}

.galeria-card:hover {
  border-color: hsl(258, 45%, 78%);
  box-shadow: 0 8px 20px -3px rgba(54, 32, 101, 0.12);
  transform: translateY(-2px);
}

.foto-imagem-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: hsl(258, 15%, 95%);
  cursor: pointer;
  overflow: hidden;
}

.foto-imagem-wrapper img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.galeria-card:hover .foto-imagem-wrapper img {
  transform: scale(1.05);
}

.foto-hover-overlay {
  position: absolute;
  inset: 0;
  background-color: rgba(27, 38, 79, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.galeria-card:hover .foto-hover-overlay {
  opacity: 1;
}

.btn-zoom-preview {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: rgba(255, 255, 255, 0.92);
  color: var(--azul-escuro, #1b264f);
  padding: 6px 14px;
  border-radius: 99px;
  font-size: 0.8rem;
  font-weight: 700;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.foto-detalhes {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
  gap: 10px;
}

.foto-descricao {
  margin: 0;
  font-size: 0.88rem;
  color: hsl(258, 20%, 25%);
  line-height: 1.4;
  font-weight: 500;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.foto-rodape-container {
  margin-top: auto;
}

@media (max-width: 576px) {
  .galeria-fotos-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}
</style>
